import _ from 'lodash'
import React, { useImperativeHandle, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'

import TextInputController from '@/components/ui/inputs/TextInputController'
import SelectInputController from '@/components/ui/inputs/SelectInputController'
import CheckboxInputController from '@/components/ui/inputs/CheckboxInputController'

import deviceIcons from '@/constants/device-icons'
import deviceTypes from '@/constants/device-types'
import { SubmitHandler, useForm, UseFormReset } from 'react-hook-form'

const formSchema = yup.object().shape({
  device: yup.string().required(),
  customName: yup.string().required(),
  iconURL: yup.string().required(),
  type: yup.string().required(),
  ptz: yup.boolean().default(false),
})

export type CustomDeviceFormData = yup.InferType<typeof formSchema>

export type CustomDeviceFormHandle = {
  reset: UseFormReset<CustomDeviceFormData>
}

export type CustomDeviceFormProps = {
  devices: MediaDeviceInfo[]
  isSubmitting: boolean
  onSubmit: SubmitHandler<CustomDeviceFormData>
}

function CustomDeviceForm(props: CustomDeviceFormProps, ref: React.ForwardedRef<CustomDeviceFormHandle>) {
  const { devices, isSubmitting, onSubmit } = props
  const { t } = useTranslation()

  const rhForm = useForm<CustomDeviceFormData>({
    defaultValues: {
      device: '',
      customName: '',
      iconURL: '',
      type: '',
      ptz: false,
    },
    resolver: yupResolver(formSchema),
  })

  useImperativeHandle(ref, () => ({
    reset: rhForm.reset
  }))

  const deviceOptions = useMemo(
    () =>
      _.map(devices, (device) => ({
        label: device.label,
        value: device.deviceId,
      })),
    [devices]
  )

  const iconOptions = useMemo(
    () =>
      deviceIcons.map((icon) => ({
        label: t(`constants.device.icon.${icon.id}`),
        value: icon.src,
      })),
    [t]
  )

  const typeOptions = useMemo(
    () =>
      deviceTypes.map((type) => ({
        label: t(`constants.device.type.${type}`),
        value: type,
      })),
    [t]
  )

  return (
    <form onSubmit={rhForm.handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid container item spacing={4}>
          <Grid item xs={6}>
            <SelectInputController control={rhForm.control} fullWidth label={t('field.device.label')} name="device" options={deviceOptions} />
          </Grid>
          <Grid item xs={6}>
            <TextInputController control={rhForm.control} fullWidth label={t('field.customName.label')} name="customName" />
          </Grid>
        </Grid>
        <Grid container item spacing={4}>
          <Grid item xs={6}>
            <SelectInputController control={rhForm.control} fullWidth label={t('field.icon.label')} name="iconURL" options={iconOptions} />
          </Grid>
          <Grid item xs={6}>
            <SelectInputController control={rhForm.control} fullWidth label={t('field.type.label')} name="type" options={typeOptions} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <CheckboxInputController control={rhForm.control} label={t('field.ptz.label')} name="ptz" />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'end', mt: 2 }}>
        <LoadingButton color="primary" disableElevation loading={isSubmitting} variant="contained" type="submit">
          {t('common:button.submit.label')}
        </LoadingButton>
      </Box>
    </form>
  )
}

export default React.forwardRef(CustomDeviceForm)
